// src/app/components/VentilationSettingsCard.tsx
import React, { useEffect, useState } from "react";
import {
  Card,
  Stack,
  Text,
  TextInput,
  Button,
  Group,
  Alert,
  Badge,
  Switch,
  Code,
  Select,
  ActionIcon,
  Tooltip,
  Divider,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { openapi } from "../../openapi";
import { useProjectId } from "../hooks/useProjectId";
import { FaPlus } from "react-icons/fa6";
import { FaTrash } from "react-icons/fa6";
import { useAppSelector } from "../app/hooks";
import { selectHttpCurrentProjct } from "../projectSelectors";

type Direction = "SUPPLY" | "EXHAUST" | "RETURN" | "TRANSFER";

type RuleForm = {
  prefix: string;
  direction: Direction;
  description?: string;
};

type FormValues = {
  compCodeDirectionRules: RuleForm[];
};

const directionOptions: { value: Direction; label: string }[] = [
  { value: "SUPPLY", label: "SUPPLY – tilluft" },
  { value: "EXHAUST", label: "EXHAUST – avtrekk" },
  { value: "RETURN", label: "RETURN – retur/omluft" },
  { value: "TRANSFER", label: "TRANSFER – overluft" },
];

export const VentilationSettingsCard: React.FC = () => {
  const projectNo = useProjectId();
  const project = useAppSelector(selectHttpCurrentProjct);
  const ventilation = project?.ventilation as
    | {
        compCodeDirectionRules?: RuleForm[];
        updatedAt?: string;
      }
    | undefined;

  const [
    updateSettings,
    { isLoading: isSaving, isSuccess: isSaveSuccess, isError: isSaveError, error: saveError, data },
  ] = openapi.endpoints.postProjectVentilationUpdateSettings.useMutation();

  const [hasTouched, setHasTouched] = useState(false);

  const form = useForm<FormValues>({
    initialValues: {
      compCodeDirectionRules:
        ventilation?.compCodeDirectionRules && ventilation.compCodeDirectionRules.length > 0
          ? ventilation.compCodeDirectionRules
          : [
              // Gi et enkelt forslag første gang (kan slettes)
              { prefix: "ST", direction: "SUPPLY", description: "Standard tilluft (ST…)" },
              { prefix: "SF", direction: "EXHAUST", description: "Standard avtrekk (SF…)" },
            ],
    },
    validate: {
      compCodeDirectionRules: (rules) => {
        if (!rules || rules.length === 0) return "Minst én regel er anbefalt";
        const hasEmptyPrefix = rules.some((r) => !r.prefix?.trim());
        if (hasEmptyPrefix) return "Prefix kan ikke være tomt";
        return null;
      },
    },
  });

  // Hydrer når prosjekt endres (f.eks. ved navigering)
  useEffect(() => {
    if (!project) return;

    const v = project?.ventilation;
    form.setValues({
      compCodeDirectionRules:
        v?.compCodeDirectionRules && v.compCodeDirectionRules.length > 0
          ? v.compCodeDirectionRules
          : form.values.compCodeDirectionRules, // behold evt. defaults
    });
    setHasTouched(false);
  }, [project?.projectNo]); // eslint-disable-line react-hooks/exhaustive-deps

  const updatedAt = data?.updatedAt ?? project?.ventilation?.updatedAt ?? ventilation?.updatedAt;

  const updatedAtText = updatedAt
    ? (() => {
        try {
          return new Date(updatedAt).toLocaleString();
        } catch {
          return String(updatedAt);
        }
      })()
    : undefined;

  const handleAddRule = () => {
    setHasTouched(true);
    form.insertListItem("compCodeDirectionRules", {
      prefix: "",
      direction: "SUPPLY",
      description: "",
    });
  };

  const handleRemoveRule = (index: number) => {
    setHasTouched(true);
    form.removeListItem("compCodeDirectionRules", index);
  };

  const handleSave = async () => {
    setHasTouched(true);
    const validation = form.validate();
    if (validation.hasErrors) return;

    const values = form.values;

    // Trim og rydd litt:
    const rules: RuleForm[] = values.compCodeDirectionRules.map((r) => ({
      prefix: r.prefix.trim(),
      direction: r.direction,
      description: r.description?.trim() || undefined,
    }));

    await updateSettings({
      body: {
        projectNo,
        ventilation: {
          compCodeDirectionRules: rules,
        },
      },
    }).unwrap();
  };

  const disableSave = isSaving;

  return (
    <Card withBorder radius="lg" p="md">
      <Stack gap="sm">
        <Text fw={600}>Ventilasjon – Comcode-regler</Text>
        <Text size="sm" c="dimmed">
          Her kan du styre hvordan <Code>Comcode</Code> tolkes for å skille mellom tilluft, avtrekk
          og andre typer luftterminaler for prosjekt <Code>{projectNo || "ukjent"}</Code>.
        </Text>

        <Alert color="blue" variant="light">
          <Stack gap={4}>
            <Text size="sm">Typisk oppsett (for mange prosjekter i dag):</Text>
            <Text size="sm" c="dimmed">
              • <Code>ST…</Code> → Tilluft (<Code>SUPPLY</Code>)
            </Text>
            <Text size="sm" c="dimmed">
              • <Code>SF…</Code> → Avtrekk (<Code>EXHAUST</Code>)
            </Text>
            <Text size="sm" c="dimmed">
              Regelen brukes for å separere typer for tilluft og avtrekk. Første regel der{" "}
              <Code>prefix</Code> matcher starten på Comcode vinner.
            </Text>
          </Stack>
        </Alert>

        <Group justify="space-between" align="center">
          {updatedAtText && (
            <Badge variant="light" color="blue">
              Sist oppdatert: {updatedAtText}
            </Badge>
          )}
        </Group>

        <Divider my="xs" />

        <Group justify="space-between" align="center">
          <Text size="sm" fw={500}>
            Regler for prefix → retning
          </Text>
          <Button
            size="xs"
            variant="subtle"
            leftSection={<FaPlus size={14} />}
            onClick={handleAddRule}
          >
            Legg til regel
          </Button>
        </Group>
        <Text size="xs" c="dimmed">
          Vi matcher Artikkelnr mot prefix i den rekkefølgen de står her. Første treff brukes. Bruk
          store/små bokstaver om hverandre – vi matcher case-insensitivt i backend.
        </Text>

        {form.errors.compCodeDirectionRules && (
          <Alert color="red" variant="light">
            {form.errors.compCodeDirectionRules}
          </Alert>
        )}

        <Stack gap="xs">
          {form.values.compCodeDirectionRules.map((rule, index) => (
            <Card key={index} withBorder radius="md" p="xs">
              <Group align="flex-start" gap="xs">
                <TextInput
                  label="Prefix"
                  placeholder="ST"
                  style={{ flex: 1 }}
                  value={rule.prefix}
                  onChange={(e) => {
                    setHasTouched(true);
                    form.setFieldValue(
                      `compCodeDirectionRules.${index}.prefix`,
                      e.currentTarget.value
                    );
                  }}
                />
                <Select
                  label="Retning"
                  data={directionOptions}
                  style={{ flex: 1.4 }}
                  value={rule.direction}
                  onChange={(value) => {
                    if (!value) return;
                    setHasTouched(true);
                    form.setFieldValue(
                      `compCodeDirectionRules.${index}.direction`,
                      value as Direction
                    );
                  }}
                />
                <TextInput
                  label="Beskrivelse (valgfritt)"
                  placeholder="f.eks. Standard tilluft"
                  style={{ flex: 2 }}
                  value={rule.description ?? ""}
                  onChange={(e) => {
                    setHasTouched(true);
                    form.setFieldValue(
                      `compCodeDirectionRules.${index}.description`,
                      e.currentTarget.value
                    );
                  }}
                />
                <Tooltip label="Fjern regel">
                  <ActionIcon
                    mt={24}
                    variant="subtle"
                    color="red"
                    onClick={() => handleRemoveRule(index)}
                  >
                    <FaTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Card>
          ))}
        </Stack>

        {isSaveError && (
          <Alert color="red" title="Kunne ikke lagre" variant="light">
            {(saveError as any)?.data?.message ||
              (saveError as any)?.message ||
              "Det oppstod en feil under oppdatering av ventilasjonsinnstillinger."}
          </Alert>
        )}
        {isSaveSuccess && (
          <Alert color="green" title="Lagret" variant="light">
            Ventilasjonsinnstillinger oppdatert for prosjekt <Code>{projectNo}</Code>.
          </Alert>
        )}

        <Group justify="flex-end" mt="xs">
          <Button type="button" onClick={handleSave} loading={isSaving} disabled={disableSave}>
            Lagre
          </Button>
        </Group>

        {hasTouched && !isSaving && !isSaveSuccess && (
          <Text size="xs" c="dimmed">
            Husk å lagre endringene dine.
          </Text>
        )}
      </Stack>
    </Card>
  );
};

export default VentilationSettingsCard;
